import React, {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useState,
} from "react";
import { createList, getMyLists } from "../../api/roster";
import { FiChevronDown, FiPlus, FiX } from "react-icons/fi";
import KitButton from "../kit/Button";
import IconButton from "../kit/IconButton";
import Chip from "../kit/Chip";
import Dropdown from "../kit/Dropdown";
import { enqueueSnackbar } from "notistack";
import { ErrorMessage } from "../Input/InputError";

const emptySlot = () => ({
  _tempId: Math.random().toString(36).slice(2),
  title: "",
  start: "",
  end: "",
  roster: "",
  status: "open",
});

// ─── PerformerSlotsEditor ─────────────────────────────────────────────────────
// Self-contained sibling of EditEvent. Exposes getSlotsData() via ref.
// eventDate: { start: ISOString, end: ISOString } — used to constrain time pickers.

const PerformerSlotsEditor = forwardRef(({ initialData, eventDate, lockedRosterId, lockedRosterName }, ref) => {
  const [rosters, setRosters] = useState([]);
  const [eventRoster, setEventRoster] = useState("");
  const [slots, setSlots] = useState([]);
  const [expandedRosterOverrides, setExpandedRosterOverrides] = useState({});
  const [showRosterCreate, setShowRosterCreate] = useState(false);
  const [newRosterName, setNewRosterName] = useState("");
  const [newRosterJoinPolicy, setNewRosterJoinPolicy] = useState("closed");
  const [newRosterVisibility, setNewRosterVisibility] = useState("public");
  const [creatingRoster, setCreatingRoster] = useState(false);
  const [validationErrors, setValidationErrors] = useState({});
  const isRosterLocked = Boolean(lockedRosterId);
  const selectedRosterId = lockedRosterId || eventRoster || "";
  const canUseLineupSlots = Boolean(selectedRosterId);
  const slotWindow = getSlotWindow(eventDate);
  const isOvernightWindow = slotWindow && slotWindow.endMinutes > 24 * 60;

  useEffect(() => {
    getMyLists()
      .then((d) => setRosters(d.lists || []))
      .catch(() => {});
  }, []);

  useEffect(() => {
    if (!initialData) return;
    setEventRoster(lockedRosterId || initialData.roster || "");
    setSlots(
      (initialData.performerSlots || []).map((s) => ({
        ...s,
        _tempId: s._id || Math.random().toString(36).slice(2),
        start: s.start ? toTimeValue(s.start) : "",
        end: s.end ? toTimeValue(s.end) : "",
        roster: s.roster || "",
      }))
    );
  }, [initialData, lockedRosterId]);

  useImperativeHandle(ref, () => ({
    getSelectedRosterInfo: () => {
      const rosterId = lockedRosterId || eventRoster || null;
      if (!rosterId) return null;
      const roster = rosters.find((item) => item._id === rosterId);
      return roster
        ? {
            _id: roster._id,
            displayName: roster.displayName,
            memberCount: roster.memberCount || 0,
          }
        : {
            _id: rosterId,
            displayName: lockedRosterName || "this roster",
            memberCount: 0,
          };
    },
    getSlotsData: (eventDateOverride) => {
      const slotDate = eventDateOverride || eventDate;
      const rosterId = lockedRosterId || eventRoster || null;
      const validation = validateSlots(slots, slotDate, { rosterRequired: Boolean(rosterId) });
      if (validation.error) {
        setValidationErrors(validation.errors || {});
        return { error: validation.error, roster: null, performerSlots: [] };
      }
      setValidationErrors({});

      return {
        roster: rosterId,
        performerSlots: rosterId
          ? slots.map((s) => ({
              ...(s._id ? { _id: s._id } : {}),
              title: s.title,
              start: normalizeTimeValue(s.start),
              end: normalizeTimeValue(s.end),
              roster: isRosterLocked ? null : s.roster || null,
              status: s.status || "open",
            }))
          : [],
      };
    },
  }));

  const addSlot = () => {
    if (!canUseLineupSlots) {
      enqueueSnackbar("Select or create a default roster before adding lineup slots", { variant: "info" });
      return;
    }
    setValidationErrors((prev) => ({ ...prev, _form: null }));
    setSlots((prev) => [...prev, emptySlot()]);
  };
  const removeSlot = (tempId) => {
    setValidationErrors((prev) => {
      if (!prev[tempId] && !prev._form) return prev;
      const next = { ...prev };
      delete next[tempId];
      delete next._form;
      return next;
    });
    setSlots((prev) => prev.filter((s) => s._tempId !== tempId));
    setExpandedRosterOverrides((prev) => {
      const next = { ...prev };
      delete next[tempId];
      return next;
    });
  };
  const updateSlot = (tempId, field, value) => {
    setValidationErrors((prev) => {
      if (!prev[tempId]?.[field] && !prev._form) return prev;
      const next = { ...prev, [tempId]: { ...(prev[tempId] || {}) } };
      delete next[tempId][field];
      if (Object.keys(next[tempId]).length === 0) delete next[tempId];
      delete next._form;
      return next;
    });
    setSlots((prev) =>
      prev.map((s) => (s._tempId === tempId ? { ...s, [field]: value } : s))
    );
  };
  const toggleRosterOverride = (tempId) =>
    setExpandedRosterOverrides((prev) => ({ ...prev, [tempId]: !prev[tempId] }));
  const handleEventRosterChange = (value) => {
    if (!value && slots.length > 0) {
      enqueueSnackbar("Remove lineup slots before clearing the default roster", { variant: "info" });
      return;
    }
    setValidationErrors((prev) => ({ ...prev, _form: null }));
    setEventRoster(value);
  };

  const handleCreateRoster = async () => {
    const name = newRosterName.trim();
    if (!name || creatingRoster) return;
    setCreatingRoster(true);
    try {
      const data = await createList(name, "venue", {
        joinPolicy: newRosterJoinPolicy,
        visibility: newRosterVisibility,
      });
      const createdRoster = data.list;
      setRosters((prev) => [{ ...createdRoster, memberCount: 0, requestCount: 0 }, ...prev]);
      setEventRoster(createdRoster._id);
      setValidationErrors((prev) => ({ ...prev, _form: null }));
      setNewRosterName("");
      setNewRosterJoinPolicy("closed");
      setNewRosterVisibility("public");
      setShowRosterCreate(false);
      enqueueSnackbar(`Roster "${createdRoster.displayName || name}" created`, { variant: "success" });
    } catch {
      enqueueSnackbar("Failed to create roster", { variant: "error" });
    } finally {
      setCreatingRoster(false);
    }
  };

  const closeRosterCreate = () => {
    setShowRosterCreate(false);
    setNewRosterName("");
    setNewRosterJoinPolicy("closed");
    setNewRosterVisibility("public");
  };

  const minTime = !isOvernightWindow && eventDate?.start ? toTimeValue(eventDate.start) : undefined;
  const maxTime = !isOvernightWindow && eventDate?.end ? toTimeValue(eventDate.end) : undefined;

  const getSlotError = (slot, field) => validationErrors[slot._tempId]?.[field];
  const rosterCreateSettings = (
    <div className="inyc-panel is-sm grid gap-kit-12 sm:grid-cols-2">
      <div>
        <div className="inyc-label is-sm">
          join access
        </div>
        <div className="flex flex-wrap gap-kit-6">
          {["closed", "open"].map((policy) => (
            <Chip key={policy} selected={newRosterJoinPolicy === policy} onClick={() => setNewRosterJoinPolicy(policy)}>
              {policy}
            </Chip>
          ))}
        </div>
        <p className="inyc-help is-sm mt-kit-8">
          {newRosterJoinPolicy === "closed"
            ? "artists request to join and need approval."
            : "artists can join instantly."}
        </p>
      </div>
      <div>
        <div className="inyc-label is-sm">
          browse visibility
        </div>
        <div className="flex flex-wrap gap-kit-6">
          {["public", "private"].map((visibility) => (
            <Chip key={visibility} selected={newRosterVisibility === visibility} onClick={() => setNewRosterVisibility(visibility)}>
              {visibility}
            </Chip>
          ))}
        </div>
        <p className="inyc-help is-sm mt-kit-8">
          {newRosterVisibility === "private"
            ? "hidden from roster browse."
            : "visible in roster browse."}
        </p>
      </div>
    </div>
  );

  return (
    <div className="mt-kit-32 px-kit-32">
      {/* Section heading — matches EditEvent's ui-label style */}
      <span className="ui-label">lineup</span>

      <div className="mt-kit-16 flex flex-col gap-kit-24">
        {/* Default roster selector */}
        {isRosterLocked ? (
          <div className="inyc-panel text-kit-xs text-[var(--inyc-primary-text-color)]">
            this event will use <span className="font-['Primary-Bold']">{lockedRosterName || "this roster"}</span> for its lineup
          </div>
        ) : rosters.length === 0 ? (
          <div className="inyc-panel is-dashed">
            <div className="inyc-label is-sm">
              create a roster for this lineup
            </div>
            <p className="inyc-help is-sm">
              artists apply through rosters. create one now, keep posting this event, then invite artists later from bookings.
            </p>
            <div className="mt-kit-12 flex flex-col gap-kit-10">
              <input
                value={newRosterName}
                onChange={(event) => setNewRosterName(event.target.value)}
                onKeyDown={(event) => {
                  if (event.key === "Enter") handleCreateRoster();
                }}
                placeholder="roster name..."
                className="inyc-field min-w-0 flex-1"
              />
              {rosterCreateSettings}
              <KitButton
                variant="primary"
                fullWidth
                onClick={handleCreateRoster}
                loading={creatingRoster}
                disabled={!newRosterName.trim()}
              >
                create roster
              </KitButton>
            </div>
          </div>
        ) : (
          <div className="flex flex-col gap-kit-8">
            <div className="flex items-center justify-between gap-kit-16">
              <label className="inyc-label is-sm !mb-0">
                select a roster
              </label>
              {showRosterCreate ? (
                <KitButton variant="secondary" size="sm" onClick={closeRosterCreate}>
                  cancel
                </KitButton>
              ) : (
                <KitButton variant="primary" size="sm" icon={<FiPlus />} onClick={() => setShowRosterCreate(true)}>
                  new roster
                </KitButton>
              )}
            </div>
            {showRosterCreate && (
              <div className="inyc-panel is-sm flex flex-col gap-kit-10">
                <input
                  value={newRosterName}
                  onChange={(event) => setNewRosterName(event.target.value)}
                  onKeyDown={(event) => {
                    if (event.key === "Enter") handleCreateRoster();
                    if (event.key === "Escape") {
                      closeRosterCreate();
                    }
                  }}
                  placeholder="new roster name..."
                  className="inyc-field min-w-0 flex-1"
                />
                {rosterCreateSettings}
                <KitButton
                  variant="primary"
                  fullWidth
                  onClick={handleCreateRoster}
                  loading={creatingRoster}
                  disabled={!newRosterName.trim()}
                >
                  create
                </KitButton>
              </div>
            )}
            <Dropdown
              aria-label="default roster"
              value={eventRoster}
              onChange={(v) => handleEventRosterChange(v)}
              options={[
                { value: "", label: "no default roster", disabled: slots.length > 0 },
                ...rosters.map((r) => ({ value: r._id, label: r.displayName })),
              ]}
            />
            <p className="inyc-help is-sm ml-kit-4">
              all slots without a specific roster will use this one
            </p>
          </div>
        )}

        {!canUseLineupSlots && (
          <div className="inyc-panel is-dashed">
            <p className="inyc-help is-sm">
              <span className="font-['Primary-Bold'] text-[var(--inyc-primary-text-color)]">
                select a default roster before adding slots.
              </span>{" "}
              slots are where artists apply, so they need a roster to belong to. you can still submit this event without roster slots.
            </p>
          </div>
        )}

        {isOvernightWindow && (
          <p className="inyc-help is-sm mt-[-0.75rem]">
            this event goes past midnight, so slot times can fall after midnight as long as they stay inside the event window.
          </p>
        )}

        {validationErrors._form && (
          <div className="inyc-panel is-sm is-danger text-kit-xs font-['Primary-Medium']">
            {validationErrors._form}
          </div>
        )}

        {/* Slot cards */}
        {canUseLineupSlots && slots.map((slot, i) => {
          const isRosterOverrideOpen = Boolean(expandedRosterOverrides[slot._tempId]);

          return (
          <div
            key={slot._tempId}
            className="inyc-panel flex flex-col gap-kit-12"
          >
            {/* Header: slot index + remove */}
            <div className="flex items-center justify-between gap-kit-8">
              <span className="inyc-label is-sm !mb-0">
                lineup slot {i + 1}
                {slot.status === "filled" && <span className="ml-kit-8 text-success">· filled</span>}
              </span>
              <IconButton label={`remove slot ${i + 1}`} variant="danger" icon={<FiX />} onClick={() => removeSlot(slot._tempId)} />
            </div>

            {/* Title */}
            <div className="flex flex-col gap-kit-4">
              <input
                aria-invalid={getSlotError(slot, "title") ? true : undefined}
                type="text"
                placeholder="lineup slot (e.g. Opener, Headliner)"
                value={slot.title}
                onChange={(e) => updateSlot(slot._tempId, "title", e.target.value)}
                className="inyc-field w-full"
              />
              {getSlotError(slot, "title") && (
                <ErrorMessage size="sm">
                  {getSlotError(slot, "title")}
                </ErrorMessage>
              )}
            </div>

            {/* Time pickers */}
            <div className="grid grid-cols-2 gap-kit-12">
              <div className="flex flex-col gap-kit-4">
                <label className="inyc-label is-sm !mb-0">start time</label>
                <input
                aria-invalid={getSlotError(slot, "start") ? true : undefined}
                  type="time"
                  value={slot.start}
                  min={minTime}
                  max={slot.end || maxTime}
                  onChange={(e) => updateSlot(slot._tempId, "start", e.target.value)}
                  className="inyc-field w-full"
                />
                {getSlotError(slot, "start") && (
                  <ErrorMessage size="sm">
                    {getSlotError(slot, "start")}
                  </ErrorMessage>
                )}
              </div>
              <div className="flex flex-col gap-kit-4">
                <label className="inyc-label is-sm !mb-0">end time</label>
                <input
                aria-invalid={getSlotError(slot, "end") ? true : undefined}
                  type="time"
                  value={slot.end}
                  min={slot.start || minTime}
                  max={maxTime}
                  onChange={(e) => updateSlot(slot._tempId, "end", e.target.value)}
                  className="inyc-field w-full"
                />
                {getSlotError(slot, "end") && (
                  <ErrorMessage size="sm">
                    {getSlotError(slot, "end")}
                  </ErrorMessage>
                )}
              </div>
            </div>

            {/* Per-slot roster override — advanced, collapsed by default */}
            {!isRosterLocked && (
            <div>
              <KitButton
                variant="tertiary"
                size="sm"
                iconRight={<FiChevronDown className={isRosterOverrideOpen ? "rotate-180" : ""} />}
                onClick={() => toggleRosterOverride(slot._tempId)}
                aria-expanded={isRosterOverrideOpen}
              >
                override roster for this slot
              </KitButton>
              {isRosterOverrideOpen && (
                <div className="mt-kit-12">
                  <Dropdown
                    aria-label={`roster for slot ${i + 1}`}
                    value={slot.roster || ""}
                    onChange={(v) => updateSlot(slot._tempId, "roster", v)}
                    options={[
                      { value: "", label: "use default roster" },
                      ...rosters.map((r) => ({ value: r._id, label: r.displayName })),
                    ]}
                  />
                </div>
              )}
            </div>
            )}
          </div>
          );
        })}

        {/* Add slot */}
        {canUseLineupSlots && (
          <KitButton variant="secondary" size="sm" icon={<FiPlus />} onClick={addSlot}>
            add lineup slot
          </KitButton>
        )}
      </div>
    </div>
  );
});

PerformerSlotsEditor.displayName = "PerformerSlotsEditor";
export default PerformerSlotsEditor;

// ─── Helpers ──────────────────────────────────────────────────────────────────

function toTimeValue(isoString) {
  if (!isoString) return "";
  const directTime = normalizeTimeValue(isoString);
  if (directTime) return directTime;
  const isoTime = String(isoString).match(/T(\d{2}:\d{2})/);
  if (isoTime && !/(Z|[+-]\d{2}:?\d{2})$/.test(String(isoString))) return isoTime[1];
  const d = new Date(isoString);
  if (Number.isNaN(d.getTime())) return "";
  const pad = (n) => String(n).padStart(2, "0");
  return `${pad(d.getUTCHours())}:${pad(d.getUTCMinutes())}`;
}

function normalizeTimeValue(timeStr) {
  if (!timeStr) return null;
  const match = String(timeStr).match(/^(\d{1,2}):(\d{2})$/);
  if (!match) return null;
  const hours = Number(match[1]);
  const minutes = Number(match[2]);
  if (hours < 0 || hours > 23 || minutes < 0 || minutes > 59) return null;
  return `${String(hours).padStart(2, "0")}:${String(minutes).padStart(2, "0")}`;
}

function timeToMinutes(timeStr) {
  const normalized = normalizeTimeValue(timeStr);
  if (!normalized) return null;
  const [hours, minutes] = normalized.split(":").map(Number);
  return hours * 60 + minutes;
}

function getSlotWindow(eventDate) {
  const start = toTimeValue(eventDate?.start);
  const end = toTimeValue(eventDate?.end);
  const startMinutes = timeToMinutes(start);
  let endMinutes = timeToMinutes(end);
  if (startMinutes === null || endMinutes === null) return null;
  if (endMinutes <= startMinutes) endMinutes += 24 * 60;
  return { startMinutes, endMinutes };
}

function normalizeMinutesForWindow(minutes, window) {
  if (minutes === null || !window) return minutes;
  if (minutes < window.startMinutes) return minutes + 24 * 60;
  return minutes;
}

function validateSlots(slots, eventDate, { rosterRequired = false } = {}) {
  const nonEmptySlots = slots.filter((slot) => slot.title?.trim() || slot.start || slot.end);
  if (rosterRequired && nonEmptySlots.length === 0) {
    return {
      error: "add at least one lineup slot for this roster",
      errors: { _form: "add at least one lineup slot for this roster" },
    };
  }

  const window = getSlotWindow(eventDate);
  if (!window) return { error: null };

  for (const slot of slots) {
    if (!slot.title?.trim() && !slot.start && !slot.end) continue;
    if (!slot.title?.trim()) {
      return {
        error: "lineup slot title is required",
        errors: { [slot._tempId]: { title: "title is required" } },
      };
    }
    const startRaw = timeToMinutes(slot.start);
    const endRaw = timeToMinutes(slot.end);
    if (slot.end && startRaw === null) {
      return {
        error: "lineup slot end time requires a start time",
        errors: { [slot._tempId]: { start: "start time is required" } },
      };
    }

    const start = normalizeMinutesForWindow(startRaw, window);
    let end = normalizeMinutesForWindow(endRaw, window);
    if (start !== null && end !== null && end <= start) end += 24 * 60;

    if (start !== null && (start < window.startMinutes || start > window.endMinutes)) {
      return {
        error: "lineup slot times must be within the event time",
        errors: { [slot._tempId]: { start: "must be within the event time" } },
      };
    }
    if (end !== null && (end < window.startMinutes || end > window.endMinutes)) {
      return {
        error: "lineup slot times must be within the event time",
        errors: { [slot._tempId]: { end: "must be within the event time" } },
      };
    }
  }

  return { error: null };
}
